// src/lib/rateCard.js
//
// The TaxStats Platform's public rate card is the price of record for the
// "new company starter package" (ratified 2026-10-12). This site must never
// TYPE those figures — it reads them from the Platform and renders them. This
// module is the one place that knows the shape of the rate card and how the
// site formats it; it is dependency-free so Node (the server, the build) and
// Vite (the pages) can both import it. It is copied into the runtime image by
// the Dockerfile like the other shared src/lib modules.
//
// How the figures reach a page: the server fetches the rate card server-side
// (server/services/rateCard.js — hourly revalidation, last-good on failure)
// and injects it into every HTML response as a <script> that sets
// window.__TS_RATE_CARD__. The build does the same for the prerendered
// snapshots (scripts/fetchRateCard.mjs + scripts/prerender.mjs). Pages read it
// synchronously with getStarter(). Nothing here fetches from the browser; the
// Platform's CORS does not allow it and it should not need to.
//
// There is deliberately NO fallback price anywhere in this file. A fallback
// would be the second copy this module exists to remove. When the rate card
// is unavailable getStarter() returns null and the pages render no figure.

export const RATE_CARD_GLOBAL = '__TS_RATE_CARD__';
export const RATE_CARD_SCRIPT_ID = 'ts-rate-card';
export const RATE_CARD_PATH = '/api/v1/instant-quote/rate-card';

// Build the rate-card URL from PLATFORM_API_URL. The repo's existing Platform
// client documents PLATFORM_API_URL as the REST base INCLUDING /api/v1
// (e.g. https://<backend>/api/v1), while the rate-card contract is written
// against the bare host — accept both so the same variable serves both.
export function rateCardUrl(base) {
  const trimmed = String(base || '').trim().replace(/\/+$/, '');
  if (!trimmed) return null;
  return /\/api\/v1$/.test(trimmed) ? `${trimmed}${RATE_CARD_PATH.replace(/^\/api\/v1/, '')}` : `${trimmed}${RATE_CARD_PATH}`;
}

function asDecimalString(value, field) {
  if (value == null || value === '') throw new Error(`rate card: starter.${field} is missing`);
  const n = Number(value);
  if (!Number.isFinite(n) || n < 0) throw new Error(`rate card: starter.${field} is not a number (${JSON.stringify(value)})`);
  return String(value);
}

function asPositiveInt(value, field, { required = true } = {}) {
  if (value == null || value === '') {
    if (required) throw new Error(`rate card: starter.${field} is missing`);
    return null;
  }
  const n = Number(value);
  if (!Number.isInteger(n) || n <= 0) throw new Error(`rate card: starter.${field} is not a positive integer (${JSON.stringify(value)})`);
  return n;
}

// Validate the Platform's JSON and keep only the fields the site renders, in
// a canonical shape. Throws on anything the pages cannot render from — a
// malformed rate card must never silently become "no price".
export function normaliseStarter(json) {
  const starter = json && typeof json === 'object' ? json.starter : null;
  if (!starter || typeof starter !== 'object') throw new Error('rate card: no "starter" package in the response');
  return Object.freeze({
    monthly: asDecimalString(starter.monthly, 'monthly'),
    minimum_months: asPositiveInt(starter.minimum_months, 'minimum_months'),
    max_age_months: asPositiveInt(starter.max_age_months, 'max_age_months', { required: false }),
    max_turnover: starter.max_turnover == null || starter.max_turnover === '' ? null : asDecimalString(starter.max_turnover, 'max_turnover'),
    max_transactions_per_month: asPositiveInt(starter.max_transactions_per_month, 'max_transactions_per_month', { required: false }),
    max_payroll_employees: asPositiveInt(starter.max_payroll_employees, 'max_payroll_employees', { required: false }),
    payroll_frequency: starter.payroll_frequency ? String(starter.payroll_frequency) : null,
    terms: starter.terms ? String(starter.terms) : null,
  });
}

// The object that is injected into the page and cached on the server.
export function normaliseRateCard(json, { fetchedAt = new Date().toISOString(), source = 'platform' } = {}) {
  return Object.freeze({ starter: normaliseStarter(json), fetchedAt, source });
}

// --- Formatting -------------------------------------------------------------

// "175" -> "£175", "149.5" -> "£149.50", "85000" -> "£85,000". The API sends
// plain decimal strings; the pages show pounds with no pence for whole amounts.
export function gbp(value) {
  if (value == null || value === '') return null;
  const n = Number(value);
  if (!Number.isFinite(n)) return null;
  const whole = Math.abs(n - Math.round(n)) < 1e-9;
  const fixed = whole ? String(Math.round(n)) : n.toFixed(2);
  const [int, frac] = fixed.split('.');
  const grouped = int.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return frac ? `£${grouped}.${frac}` : `£${grouped}`;
}

export function monthlyToPence(monthly) {
  return Math.round(Number(monthly) * 100);
}

// Monthly fee × minimum term, computed in pence so "149.99" × 6 is exact.
export function minimumSpendGbp(starter) {
  if (!starter) return null;
  return gbp((monthlyToPence(starter.monthly) * starter.minimum_months) / 100);
}

export function monthlyGbp(starter) {
  return starter ? gbp(starter.monthly) : null;
}

// "<N>-month" — used as an adjective ("a <N>-month minimum term").
export function minimumTermLabel(starter) {
  return starter ? `${starter.minimum_months}-month` : null;
}

function payrollFrequencyWord(starter) {
  const f = String(starter?.payroll_frequency || '').toLowerCase();
  return ['monthly', 'weekly', 'fortnightly', 'quarterly'].includes(f) ? f : null;
}

// The payroll line of the package, from the rate card's headcount + frequency.
// With no rate card the line names the service and no number.
export function payrollLine(starter) {
  const n = starter?.max_payroll_employees;
  const freq = payrollFrequencyWord(starter);
  if (!n) return 'PAYE registration + payroll';
  return `PAYE registration + ${freq ? `${freq} ` : ''}payroll for up to ${n} employee${n === 1 ? '' : 's'}`;
}

// What the starter package includes — rendered by the Formation page, the
// PPC lander, the checkout and the welcome email, so they can never drift.
// TaxStats delivers the accountancy; the bank account is an introduction to
// Metro, subject to their approval.
export function starterIncludes(starter) {
  return [
    'Company formed at Companies House (the £100 fee is included)',
    'Director identity verification by video appointment (Companies House ACSP) — so we can certify your proof of address for the bank',
    'Bookkeeping, year-end accounts & Corporation Tax — prepared and filed',
    'VAT registration, plus VAT returns prepared and filed',
    payrollLine(starter),
    'Self Assessment tax return for one director',
    'Confirmation statement & PSC verification statement filed',
    'Companies House & HMRC deadline monitoring',
    'Free accounting software',
    'Introduction to a business bank account (Metro) — with a relationship manager',
  ];
}

// Who the package is for, from the rate card's limits. Returns the clauses
// that the rate card actually states (none when it is unavailable).
export function starterEligibilityClauses(starter) {
  if (!starter) return [];
  const out = [];
  if (starter.max_age_months) out.push(`companies incorporated within the last ${starter.max_age_months} months with no accounts filed yet`);
  if (starter.max_turnover) out.push(`turnover up to ${gbp(starter.max_turnover)} a year`);
  if (starter.max_transactions_per_month) out.push(`up to ${starter.max_transactions_per_month} bank transactions a month`);
  return out;
}

export function starterEligibilitySentence(starter) {
  const clauses = starterEligibilityClauses(starter);
  if (!clauses.length) return null;
  const list = clauses.length === 1 ? clauses[0] : `${clauses.slice(0, -1).join(', ')} and ${clauses[clauses.length - 1]}`;
  return `For ${list}.`;
}

// --- Page injection --------------------------------------------------------

function safeJson(value) {
  return JSON.stringify(value ?? null)
    .replace(/</g, '\\u003c')
    .replace(/\u2028/g, '\\u2028')
    .replace(/\u2029/g, '\\u2029');
}

export function rateCardScriptTag(card) {
  return `<script id="${RATE_CARD_SCRIPT_ID}">window.${RATE_CARD_GLOBAL}=${safeJson(card)};</script>`;
}

// Put the current rate card into an HTML document (the SPA shell or a
// prerendered snapshot). Replaces a tag an earlier stage injected, so the
// runtime server's hourly value always wins over the build-time one.
export function injectRateCardScript(html, card) {
  const tag = rateCardScriptTag(card);
  const existing = new RegExp(`<script id="${RATE_CARD_SCRIPT_ID}">[\\s\\S]*?</script>`);
  if (existing.test(html)) return html.replace(existing, () => tag);
  const head = html.indexOf('</head>');
  if (head === -1) return `${tag}\n${html}`;
  return `${html.slice(0, head)}${tag}\n${html.slice(head)}`;
}

// --- Reading on the page ------------------------------------------------------

let testOverride; // undefined = not overridden

export function __setRateCardForTests(card) {
  testOverride = card;
}
export function __resetRateCardForTests() {
  testOverride = undefined;
}

// The injected rate card, or null when the server had none to inject.
export function getRateCard() {
  if (testOverride !== undefined) return testOverride;
  if (typeof window !== 'undefined' && window[RATE_CARD_GLOBAL]) return window[RATE_CARD_GLOBAL];
  return null;
}

export function getStarter() {
  const card = getRateCard();
  return card && card.starter ? card.starter : null;
}
